<template>
  <div class="card daily-card" :class="{ 'mobile': isMobile }">
    <div class="date-header">
      <button class="nav-btn" @click="previousDay">‹</button>
      <SingleDatePicker
        v-model="selectedDate"
        alignment="center"
        :minDate="dateRange?.startDate"
        :maxDate="dateRange?.endDate"
      >
        <template #default="{ formattedDate }">
          <h3 class="card-title">{{ formattedDate }}</h3>
        </template>
      </SingleDatePicker>
      <button class="nav-btn" @click="nextDay">›</button>
    </div>
    <div class="donut-with-legends">
      <div class="donut-chart-large">
        <canvas ref="dailyDonutCanvas"></canvas>
        <div class="center-text">
          <div class="center-label">{{ $t('dashboard.daily_visitor.visitor') }}</div>
          <div class="center-count">{{ totalVisitors.toLocaleString() }}</div>
          <div class="center-unit">{{ $t('dashboard.visitor.person_unit') }}</div>
        </div>
      </div>
      <div class="area-legends">
        <div class="legend-item" v-for="(area, index) in areaData" :key="area.name + '_' + index">
          <span class="legend-color" :style="areaFillStyle(area)"></span>
          <span class="legend-label">{{ area.name }}</span>
        </div>
      </div>
    </div>
    <div class="area-breakdown">
      <h4 class="breakdown-title">{{ $t('dashboard.daily_visitor.residential_area') }}</h4>
      <div v-if="areaData.length === 0" class="no-data-message">
        {{ $t('dashboard.daily_visitor.no_data') }}
      </div>
      <div v-else class="area-item" v-for="(area, index) in areaData" :key="area.name + '_' + index">
        <span class="area-name">{{ area.name }}</span>
        <div class="progress-bar">
          <div class="progress-fill" :style="[{ width: area.percentage + '%' }, areaFillStyle(area)]"></div>
        </div>
        <span class="area-percentage">{{ area.percentage.toFixed(1) }}%</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue';
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import { useI18n } from 'vue-i18n';
import SingleDatePicker from './SingleDatePicker.vue';
import axios from 'axios';
import { useIsMobile } from '@/utils/DeviceUtil';
import { colors } from '../../../../consts/colors';

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const { t: $t } = useI18n();
const { isMobile } = useIsMobile();

const props = defineProps({
  dateRange: {
    type: Object,
    default: null
  }
});

const dailyDonutCanvas = ref(null);

// Initialize to today or dateRange.startDate if available
const getInitialDate = () => {
  if (props.dateRange?.startDate) {
    return new Date(props.dateRange.startDate);
  }
  return new Date();
};

const selectedDate = ref(getInitialDate());
const totalVisitors = ref(0);
const areaData = ref([]);
let chartInstance = null;

// Color palette for areas
const colorPalette = [
  colors.blue[500],
  colors.blue[400],
  colors.blue[300],
  colors.blue[200],
  colors.blue[100],
  colors.gray[100],
];

// The dotted texture of 未回答: fine, dense dots on white, no base color.
// Lightness is tuned by the dot color, not the dot size — dots below
// 0.75px land on sub-pixel rendering and blur on non-retina screens.
const dotColor = colors.gray[400];
const dotRadius = 0.75;
const dotSpacing = 3;

// Build the repeating canvas pattern the chart uses for a dotted segment
const createDotPattern = () => {
  const patternCanvas = document.createElement('canvas');
  patternCanvas.width = dotSpacing;
  patternCanvas.height = dotSpacing;

  const ctx = patternCanvas.getContext('2d');
  ctx.fillStyle = colors.white;
  ctx.fillRect(0, 0, dotSpacing, dotSpacing);
  ctx.fillStyle = dotColor;
  ctx.beginPath();
  ctx.arc(dotSpacing / 2, dotSpacing / 2, dotRadius, 0, Math.PI * 2);
  ctx.fill();

  return ctx.createPattern(patternCanvas, 'repeat');
};

// Style of the legend swatch and the progress bar for an area. A dotted
// area gets the same fine dots as its chart segment, via CSS this time.
const areaFillStyle = (area) => {
  if (!area.dotted) {
    return { backgroundColor: area.color };
  }

  return {
    backgroundColor: colors.white,
    backgroundImage: `radial-gradient(circle, ${dotColor} ${dotRadius}px, transparent ${dotRadius}px)`,
    backgroundSize: `${dotSpacing}px ${dotSpacing}px`,
  };
};

// Check if date is within valid range
const isDateInRange = (date) => {
  if (!props.dateRange) return true;

  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);

  const minDate = new Date(props.dateRange.startDate);
  minDate.setHours(0, 0, 0, 0);

  const maxDate = new Date(props.dateRange.endDate);
  maxDate.setHours(0, 0, 0, 0);

  return checkDate >= minDate && checkDate <= maxDate;
};

// Navigate to previous day
const previousDay = () => {
  const newDate = new Date(selectedDate.value);
  newDate.setDate(newDate.getDate() - 1);

  if (isDateInRange(newDate)) {
    selectedDate.value = newDate;
  }
};

// Navigate to next day
const nextDay = () => {
  const newDate = new Date(selectedDate.value);
  newDate.setDate(newDate.getDate() + 1);

  if (isDateInRange(newDate)) {
    selectedDate.value = newDate;
  }
};

// Format date for API (YYYY-MM-DD)
const formatDateForAPI = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Fetch daily visitor statistics
const fetchDailyVisitor = async (date) => {
  if (!date) return;

  try {
    const dateStr = formatDateForAPI(date);
    const response = await axios.get('/api/user/statistics/dashboard/daily-visitor', {
      params: {
        target_date: dateStr
      }
    });

    if (response.data) {
      totalVisitors.value = response.data.total_visitors ?? 0;

      // Update area data with colors - Show top 5 and group the rest as "その他"
      const residentialAreas = response.data.residential_areas || [];

      // Filter out areas with empty or null names
      const validAreas = residentialAreas.filter(area => area.name && area.name.trim() !== '');

      // Separate "その他" and "未回答" from other areas
      const othersTranslation = $t('dashboard.daily_visitor.others');
      const unansweredTranslation = $t('dashboard.daily_visitor.unanswered');
      const existingOthers = validAreas.find(area => area.name === othersTranslation || area.name === 'その他');
      const existingUnanswered = validAreas.find(area => area.name === unansweredTranslation || area.name === '未回答');
      const regularAreas = validAreas.filter(area => area !== existingOthers && area !== existingUnanswered);

      // Sort regular areas by percentage in descending order
      const sortedAreas = [...regularAreas].sort((a, b) => b.percentage - a.percentage);

      // Get top 5 from regular areas (excluding "その他")
      const top5Areas = sortedAreas.slice(0, 5);
      const restAreas = sortedAreas.slice(5);

      // Map top 5 areas with colors
      const processedAreas = top5Areas.map((area, index) => ({
        name: area.name,
        count: area.count,
        percentage: area.percentage,
        color: colorPalette[index % colorPalette.length]
      }));

      // Calculate "その他" (Others) - combine areas from position 6 onwards with existing "その他"
      if (restAreas.length > 0 || existingOthers) {
        let othersCount = restAreas.reduce((sum, area) => sum + area.count, 0);
        let othersPercentage = restAreas.reduce((sum, area) => sum + area.percentage, 0);

        // Add existing "その他" data if it exists
        if (existingOthers) {
          othersCount += existingOthers.count;
          othersPercentage += existingOthers.percentage;
        }

        processedAreas.push({
          name: othersTranslation,
          count: othersCount,
          percentage: Math.round(othersPercentage * 10) / 10, // Round to 1 decimal place
          color: colors.gray[200] // Gray color for "Others"
        });
      }

      // "未回答" (no usable residence data) stays its own segment instead of
      // being folded into "その他", so the unknown share is visible as such.
      // It is drawn as a fine dot texture rather than a solid color.
      if (existingUnanswered) {
        processedAreas.push({
          name: unansweredTranslation,
          count: existingUnanswered.count,
          percentage: existingUnanswered.percentage,
          color: colors.white,
          dotted: true
        });
      }

      areaData.value = processedAreas;
      updateChart();
    }
  } catch (error) {
    console.error('Error fetching daily visitor statistics:', error);
    totalVisitors.value = 0;
    areaData.value = [];
    updateChart();
  }
};

// Watch for date changes
watch(selectedDate, (newDate) => {
  fetchDailyVisitor(newDate);
});

// Watch for dateRange changes and update selectedDate if needed
watch(() => props.dateRange, (newRange) => {
  if (newRange && !isDateInRange(selectedDate.value)) {
    selectedDate.value = new Date(newRange.startDate);
  }
}, { deep: true });

const updateChart = () => {
  if (!chartInstance) return;

  // Check if there's data
  if (areaData.value.length === 0 || totalVisitors.value === 0) {
    // Show empty state with light gray circle
    chartInstance.data.labels = [''];
    chartInstance.data.datasets[0].data = [1];
    chartInstance.data.datasets[0].backgroundColor = [colors.gray[300]];
    chartInstance.data.datasets[0].borderColor = [colors.gray[200]];
    chartInstance.data.datasets[0].borderWidth = 2;
  } else {
    // Update chart data with actual data
    chartInstance.data.labels = areaData.value.map(area => area.name);
    chartInstance.data.datasets[0].data = areaData.value.map(area => area.count);
    chartInstance.data.datasets[0].backgroundColor = areaData.value.map(area => area.dotted ? createDotPattern() : area.color);
    chartInstance.data.datasets[0].borderColor = undefined;
    chartInstance.data.datasets[0].borderWidth = 0;
  }

  chartInstance.update();
};

onMounted(() => {
  nextTick(() => {
    initializeDailyDonut();
    fetchDailyVisitor(selectedDate.value);
  });
});

const initializeDailyDonut = () => {
  if (!dailyDonutCanvas.value) return;

  const ctx = dailyDonutCanvas.value.getContext('2d');

  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: [],
      datasets: [{
        data: [],
        backgroundColor: [],
        borderWidth: 0,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: colors.white,
          titleColor: colors.textPrimary,
          bodyColor: colors.textPrimary,
          borderColor: colors.gray[200],
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: function(context) {
              // Don't show tooltip for empty state
              if (!context.label || context.label === '') {
                return null;
              }
              const count = context.parsed;
              // Calculate total from all data points in the dataset
              const total = context.dataset.data.reduce((sum, value) => sum + value, 0);
              const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
              return context.label + ': ' + count + ' ' + $t('dashboard.visitor.person_unit') + ' (' + percentage + '%)';
            }
          },
          filter: function(tooltipItem) {
            // Hide tooltip for empty state
            return tooltipItem.label && tooltipItem.label !== '';
          }
        }
      }
    }
  });
};
</script>

<style lang="scss" scoped>
.card {
  background: v-bind('colors.white');
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
  margin-bottom: 16px;
  overflow: hidden;
  min-width: 0;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: v-bind('colors.textPrimary');
  margin: 0;
}

.daily-card {
  min-width: 0;

  .date-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .nav-btn {
      background: none;
      border: none;
      font-size: 24px;
      color: v-bind('colors.gray[500]');
      cursor: pointer;
      padding: 4px 12px;

      &:hover {
        color: v-bind('colors.textPrimary');
      }
    }
  }

  .donut-with-legends {
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    margin-bottom: 24px;
    overflow: hidden;

    @media (max-width: 900px) {
      flex-direction: column;
      align-items: flex-start;
    }

    .donut-chart-large {
      flex: 0 0 auto;
      width: 280px;
      height: 280px;
      max-width: 100%;
      position: relative;

      @media (max-width: 900px) {
        margin: 0 auto;
      }

      canvas {
        max-width: 100%;
        max-height: 100%;
        position: relative;
        z-index: 2;
      }

      .center-text {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        pointer-events: none;
        z-index: 1;

        .center-label {
          font-size: 14px;
          color: v-bind('colors.gray[500]');
          margin-bottom: 8px;
        }

        .center-count {
          font-size: 48px;
          font-weight: 600;
          color: v-bind('colors.textPrimary');
          line-height: 1;
          margin-bottom: 8px;
        }

        .center-unit {
          font-size: 16px;
          color: v-bind('colors.gray[500]');
        }
      }
    }

    .area-legends {
      flex: 0 0 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 12px;
      overflow-y: auto;
      max-height: 280px;

      @media (max-width: 900px) {
        width: 100%;
        max-height: none;
      }

      .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;

        .legend-color {
          width: 12px;
          height: 12px;
          border-radius: 2px;
          flex-shrink: 0;
        }

        .legend-label {
          font-size: 13px;
          color: v-bind('colors.textPrimary');
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .no-data-message {
        font-size: 14px;
        color: v-bind('colors.gray[400]');
        text-align: center;
        padding: 20px;
      }
    }
  }

  .no-data-message {
    font-size: 14px;
    color: v-bind('colors.gray[400]');
    text-align: center;
    padding: 20px;
  }

  .breakdown-title {
    font-size: 14px;
    font-weight: 600;
    color: v-bind('colors.textPrimary');
    margin: 0 0 12px;
  }

  .area-breakdown {
    overflow: hidden;
  }

  .area-item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    min-width: 0;

    .area-name {
      flex: 0 0 140px;
      min-width: 0;
      font-size: 13px;
      color: v-bind('colors.textPrimary');
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .progress-bar {
      flex: 1;
      min-width: 60px;
      height: 8px;
      background-color: v-bind('colors.gray[300]');
      border-radius: 4px;
      overflow: hidden;

      .progress-fill {
        height: 100%;
        border-radius: 4px;
        transition: width 0.3s ease;
      }
    }

    .area-percentage {
      flex: 0 0 60px;
      text-align: right;
      font-size: 13px;
      color: v-bind('colors.gray[500]');
    }
  }
}

// Mobile design
.daily-card.mobile {
  .donut-with-legends {
    justify-content: center;
    gap: 16px;

    .donut-chart-large {
      width: 250px;
      height: 250px;
      aspect-ratio: 1;
      max-width: none;
      flex: 0 0 auto;

      canvas {
        max-width: 100%;
        max-height: 100%;
      }

      .center-text {
        .center-count {
          font-size: 36px;
        }

        .center-label {
          font-size: 12px;
        }

        .center-unit {
          font-size: 14px;
        }
      }
    }

    .area-legends {
      display: none;
    }
  }

  .area-item {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    gap: 8px 12px;
    margin-bottom: 16px;

    .area-name {
      grid-column: 1;
      grid-row: 1;
      font-size: 14px;
      flex: none;
    }

    .area-percentage {
      grid-column: 2;
      grid-row: 1;
      text-align: right;
      font-size: 14px;
      font-weight: 600;
      flex: none;
    }

    .progress-bar {
      grid-column: 1 / -1;
      grid-row: 2;
      width: 100%;
      flex: none;
    }
  }
}
</style>
